import React, { useEffect, useRef, useState, useCallback } from "react";
import videojs from "video.js";
import { usePlayer } from "../../../../contexts/PlayerContext";
import "video.js/dist/video-js.css";
import "@videojs/themes/dist/city/index.css";
import { Form, Button, Alert, Row, Col } from "react-bootstrap";

function VideoPlayerInner() {
    const videoRef = useRef(null);
    const playerRef = useRef(null);
    const retryTimeoutRef = useRef(null);

    const {
        streamId,
        activeStatus,
        backgroundImage,
        passwordProtected,
        checkCredentials,
        passwordProtectedError,
        setPasswordProtectedError,
        currentStreamURL,
    } = usePlayer();
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isVisible, setIsVisible] = useState(true);
    const [password, setPassword] = useState("");

    // --- Handlers and helpers ---
    const playerOptions = {
        autoplay: true,
        controls: true,
        responsive: true,
        fluid: true,
        liveui: true,
        poster: `${backgroundImage}`,
        sources: [
            {
                src: currentStreamURL,
                type: "application/x-mpegURL",
                withCredentials: false,
                label: "Default Stream",
            },
        ],
        errorDisplay: false,
        preload: "auto",
        html5: {
            hls: {
                startLevel: -1,
                capLevelToPlayerSize: true,
                overrideNative: true,
            },
            nativeAudioTracks: false,
            nativeVideoTracks: false,
        },
    };

    const clearRetryTimeout = () => {
        if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current);
            retryTimeoutRef.current = null;
        }
    };

    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        const isValid = await checkCredentials(password);
        if (isValid && isValid.status === "success") {
            setIsAuthenticated(true);
        } else {
            setPasswordProtectedError("Invalid password. Please try again.");
        }
    };

    // --- Player setup/teardown ---
    useEffect(() => {
        if (
            !activeStatus ||
            (passwordProtected && !isAuthenticated)
        ) {
            return;
        }

        // Setup player
        if (!playerRef.current) {
            const videoElement = document.createElement("video-js");
            videoElement.classList.add("vjs-big-play-centered");
            videoRef.current.appendChild(videoElement);

            const player = (playerRef.current = videojs(
                videoElement,
                playerOptions,
                () => {
                    videojs.log("player is ready");
                }
            ));

            player.on("loadeddata", () => setIsVisible(true));

            player.on("ended", () => {
                videojs.log("Playback ended. Retrying in 10 seconds...");
                setIsVisible(false);
                clearRetryTimeout();
                retryTimeoutRef.current = setTimeout(() => {
                    videojs.log("Retrying to load the video...");
                    player.src(playerOptions.sources);
                    player.load();
                }, 10000);
            });

            player.on("stalled", () => {
                videojs.log("Playback stalled. Retrying in 5 seconds...");
                clearRetryTimeout();
                retryTimeoutRef.current = setTimeout(() => {
                    if (player.paused() && player.buffered().length === 0) {
                        videojs.log("Playback still stalled. Retrying to load the video...");
                        player.src(playerOptions.sources);
                        player.load();
                    } else {
                        videojs.log("Playback resumed, no need for retry.");
                    }
                }, 5000);
            });

            player.on("error", () => {
                const error = player.error();
                if (error && error.code >= 1 && error.code <= 5) {
                    videojs.log(`Error code ${error.code} encountered. Retrying in 10 seconds...`);
                    setIsVisible(false);
                    clearRetryTimeout();
                    retryTimeoutRef.current = setTimeout(() => {
                        videojs.log("Retrying to load the video...");
                        player.src(playerOptions.sources);
                        player.load();
                    }, 10000);
                }
            });

            player.on("playing", () => {
                clearRetryTimeout();
            });
        } else {
            // Update source if player already exists
            playerRef.current.src(playerOptions.sources);
        }

        return () => {
            clearRetryTimeout();
            if (playerRef.current && !playerRef.current.isDisposed()) {
                playerRef.current.dispose();
                playerRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [streamId, activeStatus, isAuthenticated, passwordProtected]);

    // --- Poster update ---
    useEffect(() => {
        if (playerRef.current) {
            playerRef.current.poster(backgroundImage);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [backgroundImage]);

    // --- Retry cleanup on unmount ---
    useEffect(() => {
        return () => {
            clearRetryTimeout();
        };
    }, []);

    // --- Reset authentication on password protection toggle ---
    useEffect(() => {
        if (!passwordProtected) {
            setIsAuthenticated(true);
        } else {
            setIsAuthenticated(false);
        }
    }, [passwordProtected]);

    // --- UI rendering ---
    if (activeStatus === false) {
        return null;
    }
    if (passwordProtected && !isAuthenticated) {
        return (
            <>
                {/* Semi-transparent overlay */}
                <div
                    style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        minHeight: "100%",
                        backgroundColor: "rgba(130, 177, 211, .5)",
                        zIndex: 1,
                    }}
                    className="shadow"
                />
                {/* Form container */}
                <Col
                    xs={12}
                    md={6}
                    className="rounded bg-white shadow-lg border position-absolute top-50 start-50 translate-middle"
                    style={{
                        zIndex: 100000,
                    }}
                >
                    <h2 className="text-center">Password Protected</h2>
                    <p className="text-center text-small">
                        This stream is password protected. Please enter the
                        password to continue.
                    </p>
                    <Form onSubmit={handlePasswordSubmit}>
                        <Form.Group controlId="password" className="mb-1 m-3">
                            <Form.Control
                                type="password"
                                placeholder="Enter Password"
                                value={password}
                                onChange={(e) =>
                                    setPassword(e.target.value)
                                }
                                className="bg-light"
                            />
                        </Form.Group>
                        {passwordProtectedError && (
                            <Alert variant="danger">
                                {passwordProtectedError}
                            </Alert>
                        )}
                        <Button
                            variant="primary"
                            type="submit"
                            className="mb-2 m-3 btn-sm"
                            disabled={!password}
                        >
                            Submit
                        </Button>
                    </Form>
                </Col>
            </>
        );
    }

    return (
        <div
            data-vjs-player
            className={`custom-video-player ${isVisible ? "" : "d-none"}`}
            style={{
                position: "relative",
                width: "100%",
                height: "100%",
            }}
        >
            <div ref={videoRef} />
        </div>
    );
}

function VideoPlayer() {
    const { streamId, slug, currentStreamURL } = usePlayer();
    const [playerKey, setPlayerKey] = useState(0);
    useEffect(() => {
        setPlayerKey((k) => k + 1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentStreamURL]);

    if (streamId === "ITtLPTlcNzhP11198497973225") {
        return (
            <iframe
                src="https://vimeo.com/event/1726726/embed"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    minHeight: "100%",
                    backgroundColor: "rgba(130, 177, 211, .5)",
                    zIndex: 1,
                }}
                title="Welcome to ChurchCamLive"
            ></iframe>
        );
    }

    if (
        streamId == "0h08RWX8qnp50TBg1715076103143" ||
        streamId == "eggFr8s3cwrH1jqk1715075264247"
    ) {
        return (
            <iframe
                src={`https://churchcamlive.ie/amscameras/${slug}.html`}
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    minHeight: "100%",
                    backgroundColor: "rgba(130, 177, 211, .5)",
                    zIndex: 1,
                }}
                title={`Live Stream for ${slug}`}
            ></iframe>
        );
    }



    return <VideoPlayerInner key={playerKey} />;
}

export default VideoPlayer;
